import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useState, type FormEvent } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/auth/AuthProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { LanguageSwitcher } from "@/components/LanguageSwitcher";
import { Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import logo from "@/assets/logo.webp";
import type { TFunction } from "i18next";

function getSignupErrorMessage(error: { message: string; code?: string }, t: TFunction) {
  if (error.code === "weak_password" || /weak password|easy to guess|pwned/i.test(error.message)) {
    return {
      title: t("auth.weakPasswordTitle"),
      description: t("auth.weakPasswordDesc"),
    };
  }

  return {
    title: t("auth.signUpFailed"),
    description: error.message,
  };
}

export const Route = createFileRoute("/auth")({
  component: AuthPage,
  head: () => ({
    meta: [
      { title: "Sign in — Business Dite Ads" },
      {
        name: "description",
        content: "Sign in or create your construction company account on Business Dite Ads.",
      },
      { property: "og:title", content: "Sign in — Business Dite Ads" },
      { property: "og:url", content: "https://busditeads.com/auth" },
    ],
    links: [{ rel: "canonical", href: "https://busditeads.com/auth" }],
  }),
});

function AuthPage() {
  const navigate = useNavigate();
  const { session, loading } = useAuth();
  const [tab, setTab] = useState<"signin" | "signup">("signin");
  const { t } = useTranslation();

  useEffect(() => {
    if (!loading && session) {
      navigate({ to: "/dashboard" });
    }
  }, [session, loading, navigate]);

  return (
    <div className="flex min-h-screen items-center justify-center bg-background px-4 py-10">
      <div
        aria-hidden
        className="pointer-events-none fixed inset-0 -z-10 opacity-40"
        style={{
          background:
            "radial-gradient(60% 50% at 50% 0%, color-mix(in oklab, var(--primary) 30%, transparent), transparent 70%)",
        }}
      />
      <div className="absolute right-4 top-4">
        <LanguageSwitcher variant="outline" />
      </div>
      <div className="w-full max-w-md">
        <Link to="/" className="mb-6 flex items-center justify-center gap-2">
          <img src={logo} alt="Business Dite Ads" className="h-10 w-10 object-contain" />
          <span className="text-base font-semibold tracking-tight">
            Business <span className="text-primary">Dite Ads</span>
          </span>
        </Link>

        <Card className="p-6 shadow-[var(--shadow-soft)]">
          <h1 className="mb-4 text-center text-xl font-semibold tracking-tight">
            {tab === "signin" ? t("auth.signIn") : t("auth.createAccount")}
          </h1>
          <Tabs value={tab} onValueChange={(v) => setTab(v as "signin" | "signup")}>
            <TabsList className="grid w-full grid-cols-2">
              <TabsTrigger value="signin">{t("auth.signIn")}</TabsTrigger>
              <TabsTrigger value="signup">{t("auth.createAccount")}</TabsTrigger>
            </TabsList>

            <TabsContent value="signin" className="mt-6">
              <SignInForm />
            </TabsContent>

            <TabsContent value="signup" className="mt-6">
              <SignUpForm onCreated={() => setTab("signin")} />
            </TabsContent>
          </Tabs>
        </Card>

        <p className="mt-6 text-center text-xs text-muted-foreground">
          {t("auth.termsNoticePrefix")}{" "}
          <Link to="/terms" className="text-primary hover:underline">
            {t("landing.terms")}
          </Link>{" "}
          {t("auth.termsNoticeAnd")}{" "}
          <Link to="/privacy" className="text-primary hover:underline">
            {t("landing.privacy")}
          </Link>
          .
        </p>
      </div>
    </div>
  );
}

function GoogleButton() {
  const { t } = useTranslation();
  const handleGoogle = async () => {
    const { error } = await supabase.auth.signInWithOAuth({
      provider: "google",
      options: { redirectTo: `${window.location.origin}/dashboard` },
    });
    if (error) {
      toast.error(t("auth.googleFailed"), {
        description: error.message,
      });
    }
  };
  return (
    <Button type="button" variant="outline" className="w-full" onClick={handleGoogle}>
      <svg className="mr-2 h-4 w-4" viewBox="0 0 24 24">
        <path
          fill="#4285F4"
          d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.76h3.55c2.08-1.92 3.29-4.74 3.29-8.09Z"
        />
        <path
          fill="#34A853"
          d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.55-2.76c-.99.66-2.25 1.06-3.73 1.06-2.87 0-5.3-1.94-6.17-4.55H2.17v2.86A11 11 0 0 0 12 23Z"
        />
        <path
          fill="#FBBC05"
          d="M5.83 14.09a6.6 6.6 0 0 1 0-4.18V7.05H2.17a11 11 0 0 0 0 9.9l3.66-2.86Z"
        />
        <path
          fill="#EA4335"
          d="M12 5.38c1.62 0 3.07.56 4.21 1.64l3.15-3.15C17.45 2.1 14.97 1 12 1A11 11 0 0 0 2.17 7.05l3.66 2.86C6.7 7.32 9.13 5.38 12 5.38Z"
        />
      </svg>
      {t("auth.continueWithGoogle")}
    </Button>
  );
}

function SignInForm() {
  const { t } = useTranslation();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [resetting, setResetting] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    setSubmitting(false);
    if (error) {
      toast.error(t("auth.signInFailed"), { description: error.message });
      return;
    }
    toast.success(t("auth.welcomeBack"));
  };

  const onForgotPassword = async () => {
    if (!email) {
      toast.error(t("auth.missingEmail"), {
        description: t("auth.missingEmailDesc"),
      });
      return;
    }
    setResetting(true);
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${window.location.origin}/reset-password`,
    });
    setResetting(false);
    if (error) {
      toast.error(t("auth.resetEmailFailed"), { description: error.message });
      return;
    }
    toast.success(t("auth.resetEmailSent"), {
      description: t("auth.resetEmailDesc"),
    });
  };

  return (
    <div className="space-y-4">
      <GoogleButton />
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-card px-2 text-muted-foreground">{t("auth.or")}</span>
        </div>
      </div>
      <form onSubmit={onSubmit} className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="signin-email">{t("auth.email")}</Label>
          <Input
            id="signin-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="signin-password">{t("auth.password")}</Label>
            <button
              type="button"
              onClick={onForgotPassword}
              disabled={resetting}
              className="text-xs font-medium text-primary hover:underline disabled:opacity-50"
            >
              {resetting ? t("auth.sending") : t("auth.forgotPassword")}
            </button>
          </div>
          <div className="relative">
            <Input
              id="signin-password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              required
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pr-10"
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
        </div>
        <Button
          type="submit"
          className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
          disabled={submitting}
        >
          {submitting ? t("auth.signingIn") : t("auth.signIn")}
        </Button>
      </form>
    </div>
  );
}

function SignUpForm({ onCreated }: { onCreated: () => void }) {
  const { t } = useTranslation();
  const [companyName, setCompanyName] = useState("");
  const [fullName, setFullName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSubmitting(true);
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: `${window.location.origin}/dashboard`,
        data: {
          company_name: companyName,
          full_name: fullName,
        },
      },
    });

    if (error) {
      setSubmitting(false);
      const parsed = getSignupErrorMessage(
        {
          message: error.message,
          code: "code" in error ? String(error.code ?? "") : undefined,
        },
        t,
      );
      toast.error(parsed.title, { description: parsed.description });
      return;
    }

    const identities = data.user?.identities ?? [];
    if (data.user && identities.length === 0) {
      setSubmitting(false);
      toast.error(t("auth.emailAlreadyRegistered"), {
        description: t("auth.emailAlreadyRegisteredDesc"),
      });
      onCreated();
      return;
    }

    if (data.session) {
      toast.success(t("auth.accountCreatedSigningIn"));
      return;
    }

    setSubmitting(false);
    toast.success(t("auth.accountCreated"), {
      description: t("auth.accountCreatedConfirmEmail"),
    });
    onCreated();
  };

  return (
    <div className="space-y-4">
      <GoogleButton />
      <div className="relative">
        <div className="absolute inset-0 flex items-center">
          <span className="w-full border-t border-border" />
        </div>
        <div className="relative flex justify-center text-xs uppercase">
          <span className="bg-card px-2 text-muted-foreground">{t("auth.or")}</span>
        </div>
      </div>
      <form onSubmit={onSubmit} className="space-y-3">
        <div className="space-y-1.5">
          <Label htmlFor="signup-company">{t("auth.companyName")}</Label>
          <Input
            id="signup-company"
            placeholder={t("auth.companyNamePlaceholder")}
            required
            value={companyName}
            onChange={(e) => setCompanyName(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="signup-name">{t("auth.fullName")}</Label>
          <Input
            id="signup-name"
            placeholder={t("auth.yourNamePlaceholder")}
            required
            value={fullName}
            onChange={(e) => setFullName(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="signup-email">{t("auth.email")}</Label>
          <Input
            id="signup-email"
            type="email"
            autoComplete="email"
            required
            value={email}
            onChange={(e) => setEmail(e.target.value)}
          />
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="signup-password">{t("auth.password")}</Label>
          <div className="relative">
            <Input
              id="signup-password"
              type={showPassword ? "text" : "password"}
              autoComplete="new-password"
              required
              minLength={8}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="pr-10"
            />
            <button
              type="button"
              tabIndex={-1}
              onClick={() => setShowPassword((v) => !v)}
              aria-label={showPassword ? t("auth.hidePassword") : t("auth.showPassword")}
              className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
            </button>
          </div>
          <p className="text-xs text-muted-foreground">{t("auth.passwordPolicyHint")}</p>
        </div>
        <Button
          type="submit"
          className="w-full bg-primary text-primary-foreground hover:bg-primary/90"
          disabled={submitting}
        >
          {submitting ? t("auth.creating") : t("auth.createCompanyAccount")}
        </Button>
      </form>
    </div>
  );
}
